import { toast } from 'sonner';
import { ApiError } from '@discovr/common';
import {
  REPORT_EXPORT_ERROR_COPY,
  fetchReportExportDownload,
  reportExportErrorMessage,
  reportExportJobErrorMessage,
} from '@discovr/features';
import type { ReportExportEventName, ReportExportEventPayload } from '@discovr/types';
import { readyTitle } from './reportExportView';
import { openDownloadUrl } from '../lib/openDownloadUrl';

/** Downloads in flight, so a second click or a replayed event sends no second request. */
const downloadsInFlight = new Set<string>();

/** Jobs this tab asked for. Every open tab gets the ready event, so only the tab that clicked
 *  Export downloads automatically; the others show the toast with its Download action. */
const awaitingDownload = new Set<string>();

const MAX_NOTIFIED = 200;
/** `event:job` keys already toasted: after an SSE reconnect the server may replay events, and a toast the user dismissed must not come back. */
const notified = new Set<string>();

/** Marks the event as shown; false when it already was. */
function firstTime(event: ReportExportEventName, jobId: string): boolean {
  const key = `${event}:${jobId}`;
  if (notified.has(key)) return false;
  notified.add(key);
  if (notified.size > MAX_NOTIFIED) notified.delete(notified.values().next().value as string);
  return true;
}

const TERMINAL_EVENTS: ReportExportEventName[] = ['report_export.ready', 'report_export.failed', 'report_export.cancelled'];

/** The terminal event this tab already showed for the job, if any. SSE can
 *  deliver it before the POST that created the job returns. */
export function shownTerminalEvent(jobId: string): ReportExportEventName | undefined {
  return TERMINAL_EVENTS.find((event) => notified.has(`${event}:${jobId}`));
}

export function resetNotifiedExportEvents(): void {
  notified.clear();
  awaitingDownload.clear();
}

/** Marks a job this tab started or joined, so its ready event downloads the file here. */
export function awaitReportExportDownload(jobId: string): void {
  awaitingDownload.add(jobId);
}

export function logExportFailure(err: unknown): void {
  const details = err instanceof ApiError ? { code: err.code, httpStatus: err.httpStatus, traceId: err.traceId } : { error: err };
  console.error('Report export failed', details);
}

/** Opens a presigned URL, or says why it could not be opened. `toastId` names a toast the error
 *  replaces. */
export function startDownload(url: string, toastId?: string): void {
  if (!openDownloadUrl(url)) toast.error(REPORT_EXPORT_ERROR_COPY.invalidLink, { id: toastId });
}

/** Fetches a fresh presigned link for a finished export and starts the browser download. A
 *  failure replaces the toast named by `toastId`, else shows its own. */
export async function downloadReportExport(campaignId: string, jobId: string, toastId?: string): Promise<void> {
  if (downloadsInFlight.has(jobId)) return;
  downloadsInFlight.add(jobId);
  try {
    const { url } = await fetchReportExportDownload(campaignId, jobId);
    startDownload(url, toastId);
  } catch (err) {
    logExportFailure(err);
    toast.error(reportExportErrorMessage(err, 'download'), { id: toastId });
  } finally {
    downloadsInFlight.delete(jobId);
  }
}

function context({ file_name }: ReportExportEventPayload): { description?: string } {
  return file_name ? { description: file_name } : {};
}

/** Downloads the finished file straight away in the tab that asked for it; the toast keeps a
 *  Download action in case the browser blocked it, and for the other tabs. */
function announceReady(payload: ReportExportEventPayload): void {
  const { campaign_id, job_id } = payload;
  if (awaitingDownload.delete(job_id)) void downloadReportExport(campaign_id, job_id);
  toast.success(readyTitle(payload.warnings), {
    id: job_id,
    ...context(payload),
    action: { label: 'Download', onClick: () => void downloadReportExport(campaign_id, job_id) },
  });
}

/** Toasts for the requester's own export, and downloads it once ready. The job id is the toast id,
 *  so a replayed or reconnected event updates the toast already on screen; `started` is silent
 *  because the click already toasted. */
export function notifyReportExportEvent(event: ReportExportEventName, payload: ReportExportEventPayload): void {
  if (event === 'report_export.started' || !firstTime(event, payload.job_id)) return;
  if (event !== 'report_export.ready') awaitingDownload.delete(payload.job_id);
  if (event === 'report_export.ready') announceReady(payload);
  if (event === 'report_export.failed') {
    toast.error(reportExportJobErrorMessage(payload.error_code), { id: payload.job_id, ...context(payload) });
  }
  if (event === 'report_export.cancelled') toast.info('Export cancelled', { id: payload.job_id, ...context(payload) });
}
